import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControlLabel,
  Switch,
  Chip,
  Divider,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import {
  Add as AddIcon,
  Search as SearchIcon,
  TaskAlt as MarkReviewedIcon,
} from '@mui/icons-material'
import { resourcesAPI, contentGroupsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

const ORG_KINDS = [
  { value: 'nonprofit', label: 'Nonprofit' },
  { value: 'government', label: 'Government' },
  { value: 'institution', label: 'Institution' },
  { value: 'platform', label: 'Platform' },
]

const GEO_LEVELS = [
  { value: 'national', label: 'National' },
  { value: 'state', label: 'State' },
  { value: 'county', label: 'County' },
  { value: 'institution', label: 'Institution' },
]

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
  { value: 'waitlist', label: 'Waitlist' },
  { value: 'unknown', label: 'Unknown' },
]

const statusStyles = {
  open: '#16a34a',
  closed: '#dc2626',
  waitlist: '#d97706',
  unknown: '#71717a',
}

// Normalize list responses to an array regardless of envelope shape
const toList = (data, key) => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.[key])) return data[key]
  if (Array.isArray(data?.data)) return data.data
  return []
}

const arrayToCsv = (arr) => (Array.isArray(arr) ? arr.join(', ') : '')
const csvToArray = (str) =>
  (str || '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)

const EMPTY_FORM = {
  group_id: '',
  code: '',
  name: '',
  aka: '',
  org_kind: 'nonprofit',
  themes: '',
  cancer_types: '',
  aya_focused: false,
  caregiver_focused: false,
  geo_level: 'national',
  states: '',
  counties: '',
  eligibility_notes: '',
  phones: '',
  emails: '',
  urls: '',
  application_notes: '',
  pays_vendor_directly: '',
  benefit_notes: '',
  languages: '',
  status: 'unknown',
  last_verified_at: '',
  verification_note: '',
  source_provenance: '',
  conflicts: '',
  notes: '',
  needs_review: false,
  is_active: true,
}

const resourceToForm = (resource) => ({
  group_id: resource.group_id || '',
  code: resource.code || '',
  name: resource.name || '',
  aka: resource.aka || '',
  org_kind: resource.org_kind || 'nonprofit',
  themes: arrayToCsv(resource.themes),
  cancer_types: arrayToCsv(resource.cancer_types),
  aya_focused: !!resource.aya_focused,
  caregiver_focused: !!resource.caregiver_focused,
  geo_level: resource.geo_level || 'national',
  states: arrayToCsv(resource.states),
  counties: arrayToCsv(resource.counties),
  eligibility_notes: resource.eligibility_notes || '',
  phones: arrayToCsv(resource.phones),
  emails: arrayToCsv(resource.emails),
  urls: arrayToCsv(resource.urls),
  application_notes: resource.application_notes || '',
  pays_vendor_directly:
    resource.pays_vendor_directly === true
      ? 'yes'
      : resource.pays_vendor_directly === false
        ? 'no'
        : '',
  benefit_notes: resource.benefit_notes || '',
  languages: arrayToCsv(resource.languages),
  status: resource.status || 'unknown',
  last_verified_at: resource.last_verified_at ? resource.last_verified_at.substring(0, 10) : '',
  verification_note: resource.verification_note || '',
  source_provenance: arrayToCsv(resource.source_provenance),
  conflicts: resource.conflicts || '',
  notes: resource.notes || '',
  needs_review: !!resource.needs_review,
  is_active: resource.is_active !== false,
})

const formToPayload = (form) => ({
  group_id: form.group_id || null,
  code: form.code,
  name: form.name,
  aka: form.aka || null,
  org_kind: form.org_kind,
  themes: csvToArray(form.themes),
  cancer_types: csvToArray(form.cancer_types),
  aya_focused: form.aya_focused,
  caregiver_focused: form.caregiver_focused,
  geo_level: form.geo_level,
  states: csvToArray(form.states),
  counties: csvToArray(form.counties),
  eligibility_notes: form.eligibility_notes || null,
  phones: csvToArray(form.phones),
  emails: csvToArray(form.emails),
  urls: csvToArray(form.urls),
  application_notes: form.application_notes || null,
  pays_vendor_directly:
    form.pays_vendor_directly === 'yes' ? true : form.pays_vendor_directly === 'no' ? false : null,
  benefit_notes: form.benefit_notes || null,
  languages: csvToArray(form.languages),
  status: form.status,
  last_verified_at: form.last_verified_at || null,
  verification_note: form.verification_note || null,
  source_provenance: csvToArray(form.source_provenance),
  conflicts: form.conflicts || null,
  notes: form.notes || null,
  needs_review: form.needs_review,
  is_active: form.is_active,
})

function Resources() {
  const { hasRole } = useAuth()
  const canEdit = hasRole(['system_admin', 'administrator', 'clinical_designer'])
  const [resources, setResources] = useState([])
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Filter state — needs_review defaults ON so the review queue shows first
  const [searchTerm, setSearchTerm] = useState('')
  const [filterGroup, setFilterGroup] = useState('')
  const [filterNeedsReview, setFilterNeedsReview] = useState(true)
  const [filterTheme, setFilterTheme] = useState('')

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingResource, setEditingResource] = useState(null)
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [resourcesResponse, groupsResponse] = await Promise.all([
        resourcesAPI.getAll({ include_inactive: true }),
        contentGroupsAPI.getAll(true),
      ])
      setResources(toList(resourcesResponse.data, 'resources'))
      setGroups(toList(groupsResponse.data, 'content_groups'))
    } catch (err) {
      console.error('Error fetching resources:', err)
      setError('Failed to load resources. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const groupName = (groupId) => groups.find((g) => g.id === groupId)?.name || '—'

  // Theme options derived from loaded data
  const themeOptions = [...new Set(resources.flatMap((r) => r.themes || []))].sort()

  const filteredResources = resources.filter((resource) => {
    const matchesSearch =
      !searchTerm ||
      resource.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.aka?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (resource.themes || []).some((t) => t.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesGroup = !filterGroup || resource.group_id === filterGroup
    const matchesNeedsReview = !filterNeedsReview || resource.needs_review
    const matchesTheme = !filterTheme || (resource.themes || []).includes(filterTheme)
    return matchesSearch && matchesGroup && matchesNeedsReview && matchesTheme
  })

  const handleOpenDialog = (resource = null) => {
    if (resource) {
      setEditingResource(resource)
      setFormData(resourceToForm(resource))
    } else {
      setEditingResource(null)
      setFormData(EMPTY_FORM)
    }
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingResource(null)
    setFormData(EMPTY_FORM)
  }

  const saveResource = async (form) => {
    try {
      setSaving(true)
      setError(null)
      const payload = formToPayload(form)
      if (editingResource) {
        await resourcesAPI.update(editingResource.id, payload)
      } else {
        await resourcesAPI.create(payload)
      }
      handleCloseDialog()
      fetchData()
    } catch (err) {
      console.error('Error saving resource:', err)
      setError(err.response?.data?.message || 'Failed to save resource. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleSave = () => saveResource(formData)

  // Save the current edits with needs_review cleared
  const handleMarkReviewed = () => saveResource({ ...formData, needs_review: false })

  const updateField = (field) => (e) => setFormData({ ...formData, [field]: e.target.value })
  const updateSwitch = (field) => (e) => setFormData({ ...formData, [field]: e.target.checked })

  const columns = [
    {
      field: 'name',
      headerName: 'Resource',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {params.row.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontFamily="monospace" fontSize="0.6875rem">
            {params.row.code}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'org_kind',
      headerName: 'Org',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontSize="0.8125rem">
          {ORG_KINDS.find((k) => k.value === params.value)?.label || params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'themes',
      headerName: 'Themes',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => {
        const themes = params.value || []
        return (
          <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
            {themes.slice(0, 3).map((theme) => (
              <Chip key={theme} label={theme} size="small" sx={{ fontSize: '0.6875rem', height: 20 }} />
            ))}
            {themes.length > 3 && (
              <Typography variant="caption" color="text.secondary">
                +{themes.length - 3}
              </Typography>
            )}
          </Box>
        )
      },
    },
    {
      field: 'geo_level',
      headerName: 'Geo',
      width: 90,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" fontSize="0.8125rem">
          {GEO_LEVELS.find((g) => g.value === params.value)?.label || params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 90,
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontSize="0.8125rem"
          sx={{ color: statusStyles[params.value] || statusStyles.unknown }}
        >
          {STATUS_OPTIONS.find((s) => s.value === params.value)?.label || params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'needs_review',
      headerName: 'Review',
      width: 110,
      renderCell: (params) =>
        params.value ? (
          <Chip label="Needs review" size="small" color="warning" sx={{ fontSize: '0.6875rem', height: 20 }} />
        ) : (
          <Typography variant="body2" color="text.secondary" fontSize="0.8125rem">
            Reviewed
          </Typography>
        ),
    },
    {
      field: 'group_id',
      headerName: 'Group',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" fontSize="0.8125rem">
          {groupName(params.value)}
        </Typography>
      ),
    },
  ]

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h5">Resources</Typography>
          <Typography variant="body2" color="text.secondary">
            Review and manage support resources surfaced to patients through pathway content.
          </Typography>
        </Box>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            New Resource
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search resources..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Group</InputLabel>
            <Select value={filterGroup} label="Group" onChange={(e) => setFilterGroup(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Theme</InputLabel>
            <Select value={filterTheme} label="Theme" onChange={(e) => setFilterTheme(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {themeOptions.map((theme) => (
                <MenuItem key={theme} value={theme}>
                  {theme}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={filterNeedsReview}
                onChange={(e) => setFilterNeedsReview(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Needs review only</Typography>}
          />
          {(searchTerm || filterGroup || filterTheme || !filterNeedsReview) && (
            <Button
              size="small"
              onClick={() => {
                setSearchTerm('')
                setFilterGroup('')
                setFilterTheme('')
                setFilterNeedsReview(true)
              }}
            >
              Reset
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {filteredResources.length} of {resources.length} resources
          </Typography>
        </Box>
      </Paper>

      {/* Resource List */}
      <Paper>
        <DataGrid
          rows={filteredResources}
          columns={columns}
          pageSize={15}
          rowsPerPageOptions={[15, 25, 50]}
          autoHeight
          disableSelectionOnClick
          getRowId={(row) => row.id}
          onRowClick={(params) => handleOpenDialog(params.row)}
          getRowClassName={(params) => (params.row.needs_review ? 'row-needs-review' : '')}
          sx={{
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .row-needs-review': {
              bgcolor: 'rgba(237, 108, 2, 0.08)',
              '&:hover': { bgcolor: 'rgba(237, 108, 2, 0.14)' },
            },
          }}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingResource ? (canEdit ? 'Edit Resource' : 'View Resource') : 'Create Resource'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {formData.conflicts && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                This resource has unresolved conflicts from ingestion. Review the Conflicts field
                below before marking it reviewed.
              </Alert>
            )}

            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Code"
                value={formData.code}
                onChange={updateField('code')}
                margin="normal"
                required
                disabled={!canEdit}
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />
              <FormControl fullWidth margin="normal" disabled={!canEdit}>
                <InputLabel>Group</InputLabel>
                <Select value={formData.group_id} label="Group" onChange={updateField('group_id')}>
                  <MenuItem value="">None</MenuItem>
                  {groups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={updateField('name')}
                margin="normal"
                required
                disabled={!canEdit}
              />
              <TextField
                fullWidth
                label="Also known as"
                value={formData.aka}
                onChange={updateField('aka')}
                margin="normal"
                disabled={!canEdit}
              />
            </Box>
            <Box display="flex" gap={2}>
              <FormControl fullWidth margin="normal" disabled={!canEdit}>
                <InputLabel>Organization Kind</InputLabel>
                <Select value={formData.org_kind} label="Organization Kind" onChange={updateField('org_kind')}>
                  {ORG_KINDS.map((kind) => (
                    <MenuItem key={kind.value} value={kind.value}>
                      {kind.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal" disabled={!canEdit}>
                <InputLabel>Status</InputLabel>
                <Select value={formData.status} label="Status" onChange={updateField('status')}>
                  {STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              fullWidth
              label="Themes"
              value={formData.themes}
              onChange={updateField('themes')}
              margin="normal"
              disabled={!canEdit}
              helperText="Comma-separated (e.g., financial, transportation, lodging)"
            />
            <TextField
              fullWidth
              label="Cancer Types"
              value={formData.cancer_types}
              onChange={updateField('cancer_types')}
              margin="normal"
              disabled={!canEdit}
              helperText="Comma-separated"
            />
            <Box display="flex" gap={3} sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.aya_focused}
                    onChange={updateSwitch('aya_focused')}
                    disabled={!canEdit}
                  />
                }
                label="AYA focused"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.caregiver_focused}
                    onChange={updateSwitch('caregiver_focused')}
                    disabled={!canEdit}
                  />
                }
                label="Caregiver focused"
              />
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Geography & Eligibility
            </Typography>
            <Box display="flex" gap={2}>
              <FormControl fullWidth margin="normal" disabled={!canEdit}>
                <InputLabel>Geo Level</InputLabel>
                <Select value={formData.geo_level} label="Geo Level" onChange={updateField('geo_level')}>
                  {GEO_LEVELS.map((level) => (
                    <MenuItem key={level.value} value={level.value}>
                      {level.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="States"
                value={formData.states}
                onChange={updateField('states')}
                margin="normal"
                disabled={!canEdit}
                helperText="Comma-separated (e.g., FL, NY)"
              />
              <TextField
                fullWidth
                label="Counties"
                value={formData.counties}
                onChange={updateField('counties')}
                margin="normal"
                disabled={!canEdit}
                helperText="Comma-separated"
              />
            </Box>
            <TextField
              fullWidth
              label="Eligibility Notes"
              value={formData.eligibility_notes}
              onChange={updateField('eligibility_notes')}
              margin="normal"
              multiline
              rows={2}
              disabled={!canEdit}
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Contact & Benefits
            </Typography>
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Phones"
                value={formData.phones}
                onChange={updateField('phones')}
                margin="normal"
                disabled={!canEdit}
                helperText="Comma-separated"
              />
              <TextField
                fullWidth
                label="Emails"
                value={formData.emails}
                onChange={updateField('emails')}
                margin="normal"
                disabled={!canEdit}
                helperText="Comma-separated"
              />
            </Box>
            <TextField
              fullWidth
              label="URLs"
              value={formData.urls}
              onChange={updateField('urls')}
              margin="normal"
              disabled={!canEdit}
              helperText="Comma-separated"
            />
            <TextField
              fullWidth
              label="Application Notes"
              value={formData.application_notes}
              onChange={updateField('application_notes')}
              margin="normal"
              multiline
              rows={2}
              disabled={!canEdit}
            />
            <Box display="flex" gap={2}>
              <FormControl fullWidth margin="normal" disabled={!canEdit}>
                <InputLabel>Pays Vendor Directly</InputLabel>
                <Select
                  value={formData.pays_vendor_directly}
                  label="Pays Vendor Directly"
                  onChange={updateField('pays_vendor_directly')}
                >
                  <MenuItem value="">Unknown</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Languages"
                value={formData.languages}
                onChange={updateField('languages')}
                margin="normal"
                disabled={!canEdit}
                helperText="Comma-separated"
              />
            </Box>
            <TextField
              fullWidth
              label="Benefit Notes"
              value={formData.benefit_notes}
              onChange={updateField('benefit_notes')}
              margin="normal"
              multiline
              rows={2}
              disabled={!canEdit}
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Verification & Review
            </Typography>
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Last Verified"
                type="date"
                value={formData.last_verified_at}
                onChange={updateField('last_verified_at')}
                margin="normal"
                disabled={!canEdit}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Verification Note"
                value={formData.verification_note}
                onChange={updateField('verification_note')}
                margin="normal"
                disabled={!canEdit}
              />
            </Box>
            <TextField
              fullWidth
              label="Source Provenance"
              value={formData.source_provenance}
              onChange={updateField('source_provenance')}
              margin="normal"
              disabled={!canEdit}
              helperText="Comma-separated list of where this resource's data came from"
            />
            <TextField
              fullWidth
              label="Conflicts"
              value={formData.conflicts}
              onChange={updateField('conflicts')}
              margin="normal"
              multiline
              rows={2}
              disabled={!canEdit}
              color="warning"
              focused={!!formData.conflicts}
              helperText={
                formData.conflicts
                  ? 'Unresolved data conflicts — resolve and clear this field'
                  : 'Conflicting data found during ingestion (empty = none)'
              }
            />
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={updateField('notes')}
              margin="normal"
              multiline
              rows={2}
              disabled={!canEdit}
            />
            <Box display="flex" gap={3} sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.needs_review}
                    onChange={updateSwitch('needs_review')}
                    disabled={!canEdit}
                  />
                }
                label="Needs review"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={updateSwitch('is_active')}
                    disabled={!canEdit}
                  />
                }
                label="Active"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{canEdit ? 'Cancel' : 'Close'}</Button>
          {canEdit && editingResource && formData.needs_review && (
            <Button
              onClick={handleMarkReviewed}
              variant="contained"
              color="success"
              startIcon={<MarkReviewedIcon />}
              disabled={saving || !formData.code || !formData.name}
            >
              {saving ? 'Saving...' : 'Mark Reviewed'}
            </Button>
          )}
          {canEdit && (
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || !formData.code || !formData.name}
            >
              {saving ? 'Saving...' : editingResource ? 'Update' : 'Create'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Resources
